import { every, filter, get } from 'lodash'

import * as Bowser from 'bowser'
import {
  fetchRateTablesFromApplication,
  redirectToOffer,
  stepSelected,
  trackGoogleAnalyticsEvent,
  updateLead,
} from 'common/actions'
import {
  CommonDispatch,
  FieldChangedOptions,
  FieldData,
  FieldDataSources,
  FieldDataValue,
  FieldKey,
  isUuid,
  MultiProductDispatch,
} from 'embed/constants'
import { CommonState, MultiProductState } from 'embed/reducers'
import { QuestionMachineConfig } from 'embed/constants/life-insurance'
import { validationDataSelector } from 'embed/selectors'
import {
  applicationLifeInsuranceRouterQuestionsGroupsSelector,
  applicationLifeInsuranceRouterQuestionsMachineConfigSelector,
  applicationLifeInsuranceRouterQuestionsQuestionsSelector,
  lifeInsuranceApplicationFieldsSelector,
} from 'embed/selectors/application'
import Session from 'common/services/Session'
import { ValidationData } from 'common/services/Validation'
import { getQuestionMachineConfig, getResponsePathWithSteps } from 'embed/utils/life-insurance'
import { Offer } from 'embed/constants/models'
import {
  Filter,
  GoogleAnalyticsActions,
  OfferType,
  Product,
  ProductStep,
  PublishKey,
  RedirectType,
  TrackingKey,
  TrackingKeyApplicationComplete,
} from 'even-ts-static'
import { findField } from 'embed/reducers/application'
import { LeadEventMetadata } from './'
import { sendValidationErrorOnBlur } from 'common/services/RUM/datadogRum'
import Tracker from 'common/services/Tracker'

// Action type keys
export enum ActionTypeKeys {
  APPLICATION_COMPLETE = 'APPLICATION_COMPLETE',
  APPLICATION_FIELD_BLURRED = 'APPLICATION_FIELD_BLURRED',
  APPLICATION_FIELD_CHANGED = 'APPLICATION_FIELD_CHANGED',
  APPLICATION_FIELD_LOADING_START = 'APPLICATION_FIELD_LOADING_START',
  APPLICATION_FIELD_LOADING_END = 'APPLICATION_FIELD_LOADING_END',
  APPLICATION_FIELD_REGISTERED = 'APPLICATION_FIELD_REGISTERED',
  APPLICATION_FIELD_SHOW_VALIDATION_STATUS = 'APPLICATION_FIELD_SHOW_VALIDATION_STATUS',
  APPLICATION_FIELD_UNREGISTERED = 'APPLICATION_FIELD_UNREGISTERED',
  APPLICATION_FIELD_VALIDATE = 'APPLICATION_FIELD_VALIDATE',
  APPLICATION_UPDATE_VALIDATION_STATUS = 'APPLICATION_UPDATE_VALIDATION_STATUS',
  APPLICATION_QUESTION_MACHINE_CONFIG_CALCULATED = 'APPLICATION_QUESTION_MACHINE_CONFIG_CALCULATED',
  APPLICATION_CALCULATE_QUESTION_MACHINE_CONFIG = 'APPLICATION_CALCULATE_QUESTION_MACHINE_CONFIG ',
}

// Action types
export interface ApplicationComplete {
  product: Product
  publishKey: PublishKey.ApplicationComplete
  trackingKey: TrackingKeyApplicationComplete
  type: ActionTypeKeys.APPLICATION_COMPLETE
}

// Action types
export interface ApplicationFieldBlurred {
  key: FieldKey
  type: ActionTypeKeys.APPLICATION_FIELD_BLURRED
  product: Product
}

export interface ApplicationFieldChanged {
  key: FieldKey
  options?: FieldChangedOptions
  type: ActionTypeKeys.APPLICATION_FIELD_CHANGED
  product: Product
  value: number | string
  populatedByPlaid: boolean | undefined
}

export interface ApplicationFieldLoadingStart {
  key: FieldKey
  type: ActionTypeKeys.APPLICATION_FIELD_LOADING_START
  product: Product
}

export interface ApplicationFieldLoadingEnd {
  key: FieldKey
  type: ActionTypeKeys.APPLICATION_FIELD_LOADING_END
  product: Product
}

export interface ApplicationFieldRegistered {
  key: FieldKey
  product: Product
  type: ActionTypeKeys.APPLICATION_FIELD_REGISTERED
  validationData: ValidationData
}

export interface ApplicationFieldShowValidationStatus {
  key: FieldKey
  product: Product
  show: boolean
  type: ActionTypeKeys.APPLICATION_FIELD_SHOW_VALIDATION_STATUS
}

export interface ApplicationFieldUnregistered {
  key: FieldKey
  product: Product
  type: ActionTypeKeys.APPLICATION_FIELD_UNREGISTERED
}

export interface ApplicationFieldValidate {
  key: FieldKey
  product: Product
  shouldShowValidationStatus: boolean
  type: ActionTypeKeys.APPLICATION_FIELD_VALIDATE
  validationData: ValidationData
  value: number | string | boolean
}

export interface ApplicationUpdateValidationStatus {
  product: Product
  type: ActionTypeKeys.APPLICATION_UPDATE_VALIDATION_STATUS
}

export interface ApplicationCalculateQuestionMachineConfig {
  type: ActionTypeKeys.APPLICATION_CALCULATE_QUESTION_MACHINE_CONFIG
}

export interface ApplicationQuestionMachineConfigCalculated {
  type: ActionTypeKeys.APPLICATION_QUESTION_MACHINE_CONFIG_CALCULATED
  machineConfig: QuestionMachineConfig
}

export type ActionTypes =
  | ApplicationComplete
  | ApplicationFieldBlurred
  | ApplicationFieldChanged
  | ApplicationFieldLoadingStart
  | ApplicationFieldLoadingEnd
  | ApplicationFieldRegistered
  | ApplicationFieldShowValidationStatus
  | ApplicationFieldUnregistered
  | ApplicationFieldValidate
  | ApplicationUpdateValidationStatus
  | ApplicationCalculateQuestionMachineConfig
  | ApplicationQuestionMachineConfigCalculated

// Action creators
export const applicationFieldShowValidationStatus = (product: Product, key: FieldKey, show: boolean) => ({
  key,
  product,
  show,
  type: ActionTypeKeys.APPLICATION_FIELD_SHOW_VALIDATION_STATUS,
})

export const applicationFieldValidate = (
  product: Product,
  key: FieldKey,
  value: FieldDataValue,
  shouldShowValidationStatus: boolean,
) => (dispatch, getState) => {
  const state = getState()
  const validationData = validationDataSelector(state)
  dispatch({
    key,
    product,
    shouldShowValidationStatus,
    type: ActionTypeKeys.APPLICATION_FIELD_VALIDATE,
    validationData,
    value,
  })
}

export const applicationFieldBlurred = (product: Product, key: FieldKey) => (dispatch, getState): void => {
  const state = getState()

  const isValid = state.application[product]?.fields?.[key]?.isValid
  if (!isValid) {
    dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.ValidationError, undefined, undefined, key))
    const trackingData = {
      key: key,
      product: product,
    }
    Tracker.trackEvent({
      action: TrackingKey.ValidationErrorOnBlur,
      data: trackingData,
      timestamp: new Date(),
    })
    sendValidationErrorOnBlur(trackingData)
  }

  const allCompletedFieldsAreValid = every(
    get(state, `application.${product}.fields`, {}),
    (field) => !field.shouldShowValidationStatus || field.isValid,
  )

  if (allCompletedFieldsAreValid) {
    dispatch(updateLead(product))
  }

  dispatch({
    key,
    product,
    type: ActionTypeKeys.APPLICATION_FIELD_BLURRED,
  })
}

export const applicationFieldChanged = (
  product: Product,
  key: FieldKey,
  value: FieldDataValue,
  shouldShowValidationStatus = false,
  populatedByPlaid = false,
  options?: FieldChangedOptions,
) => async (dispatch, getState) => {
  const state = getState()
  const wasPreviouslyPopulatedFromPlaid =
    state.application[product].fields[key]?.dataSource?.id === FieldDataSources.Plaid.id
  const isBeingPopulatedByTheUser = options?.dataSource?.id === undefined

  let trackingKey
  let trackingData
  if (wasPreviouslyPopulatedFromPlaid && isBeingPopulatedByTheUser) {
    trackingKey = TrackingKey.PlaidApplicationFieldChanged
    trackingData = {
      fields: key,
    }
  }

  const promise = new Promise((resolve) => {
    resolve(
      dispatch({
        key,
        options,
        populatedByPlaid,
        product,
        trackingData,
        trackingKey,
        type: ActionTypeKeys.APPLICATION_FIELD_CHANGED,
        value,
      }),
    )
  })

  return promise
    .then(() => dispatch(applicationFieldValidate(product, key, value, shouldShowValidationStatus)))
    .catch(() => new Error('applicationFieldChanged dispatch failed'))
}

export const applicationFieldLoadingStart = (product: Product, key: FieldKey) => (dispatch) => {
  dispatch({
    key,
    product,
    type: ActionTypeKeys.APPLICATION_FIELD_LOADING_START,
  })
}

export const applicationFieldLoadingEnd = (product: Product, key: FieldKey) => (dispatch) => {
  dispatch({
    key,
    product,
    type: ActionTypeKeys.APPLICATION_FIELD_LOADING_END,
  })
}

export const applicationFieldRegistered = (product: Product, key: FieldKey) => (dispatch, getState) => {
  const state = getState()
  const validationData = validationDataSelector(state)
  dispatch({
    key,
    product,
    type: ActionTypeKeys.APPLICATION_FIELD_REGISTERED,
    validationData,
  })
}

export const applicationFieldUnregistered = (product: Product, key: FieldKey) => ({
  key,
  product,
  type: ActionTypeKeys.APPLICATION_FIELD_UNREGISTERED,
})

export const applicationComplete = (product: Product, state: CommonState) => (dispatch) => {
  const getField = (field: Filter) => `application.${product}.fields[${field}].value`
  let trackingKey
  let GAvalue
  let GAcategory
  let extraMetadata: LeadEventMetadata = {}
  let trackingData = {}
  switch (product) {
    case Product.CreditCard:
      trackingKey = TrackingKey.CreditCardApplicationComplete
      const creditScore = get(state, getField(Filter.CreditScore))
      GAvalue = creditScore
      GAcategory = Product.CreditCard
      trackingData = { creditScore }
      break
    case Product.Loan:
      trackingKey = TrackingKey.LoanApplicationComplete
      extraMetadata = {
        credit_score: get(state, getField(Filter.CreditScore)),
        isMobile: Bowser.mobile,
        loan_amount: get(state, getField(Filter.LoanAmount)),
        os: `${Bowser.osname} - ${Bowser.name} -  ${Bowser.version}`,
        purpose: get(state, getField(Filter.LoanPurpose)),
        uuid: Session.getLeadUuid(),
      }

      // Plaid metadata
      const plaidPopulatedFields = filter(
        get(state, `application.${product}.fields`, {}),
        (fieldData: FieldData) => fieldData.populatedByPlaid,
      )
      const plaidPopulatedThenChangedFields = filter(
        get(state, `application.${product}.fields`, {}),
        (fieldData: FieldData) => fieldData.populatedByPlaidThenChanged,
      )
      if (Object.keys(plaidPopulatedFields).length > 0) {
        extraMetadata.plaid_populated_fields_count = Object.keys(plaidPopulatedFields).length
        extraMetadata.plaid_populated_then_changed_fields_count = Object.keys(plaidPopulatedThenChangedFields).length
      }

      break
    case Product.Mortgage:
      trackingKey = TrackingKey.MortgageApplicationComplete
      break
    case Product.Deposits:
      trackingKey = TrackingKey.DepositsApplicationComplete
      break
    default:
  }

  dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.ApplicationSubmitted, GAvalue, GAcategory))
  dispatch({
    lead: extraMetadata,
    product,
    publishKey: PublishKey.ApplicationComplete,
    trackingData,
    trackingKey,
    type: ActionTypeKeys.APPLICATION_COMPLETE,
  })
}

export const applicationValidate = (product: Product, step?: ProductStep, offer?: Offer, offerType?: OfferType) => (
  dispatch: MultiProductDispatch,
  getState: () => MultiProductState,
) => {
  const state = getState()
  const validationData = validationDataSelector(state)

  let isApplicationValid = true
  Object.entries(state.application?.[product]?.fields ?? {}).forEach(([fieldKey, fieldData]) => {
    if (!isUuid(fieldKey)) {
      const field = findField(fieldKey)
      const value = fieldData.value ?? ''

      // Refresh the validation state of each field
      dispatch(applicationFieldValidate(product, fieldKey, value, true))

      // Determine if the application is invalid
      const isFieldValid = !field || field.validate(value, validationData)
      if (!isFieldValid) {
        isApplicationValid = false
      }
    }
  })

  // Validate life insurance router questions
  isApplicationValid = validateLifeInsuranceRouterQuestions(isApplicationValid, product, state)

  if (isApplicationValid) {
    dispatch(fetchRateTablesFromApplication(product))
    dispatch(applicationComplete(product, state))

    // Either redirect them to the offer or send them to the next step
    if (step !== undefined) {
      dispatch(stepSelected(product, step))
    } else if (offer !== undefined && offerType !== undefined) {
      dispatch(redirectToOffer(offer, offerType, RedirectType.MANUAL))
    }
  }
}

const validateLifeInsuranceRouterQuestions = (
  isApplicationValid: boolean,
  product: Product,
  state: MultiProductState,
) => {
  const isLifeInsurance = product === Product.LifeInsurance
  const isComplete =
    isLifeInsurance &&
    getResponsePathWithSteps(
      applicationLifeInsuranceRouterQuestionsMachineConfigSelector(state),
      lifeInsuranceApplicationFieldsSelector(state),
    ).isComplete
  if (isLifeInsurance && !isComplete) {
    return false
  } else {
    return isApplicationValid
  }
}

export const applicationUpdateValidationStatus = (product: Product): ApplicationUpdateValidationStatus => ({
  product,
  type: ActionTypeKeys.APPLICATION_UPDATE_VALIDATION_STATUS,
})

export const applicationCalculateQuestionMachineConfig = () => (
  dispatch: CommonDispatch,
  getState: () => CommonState,
) => {
  const state = getState()
  const groups = applicationLifeInsuranceRouterQuestionsGroupsSelector(state)
  const questions = applicationLifeInsuranceRouterQuestionsQuestionsSelector(state)
  const newQuestionMachineConfig = getQuestionMachineConfig(questions, groups)
  dispatch(applicationQuestionMachineConfigCalculated(newQuestionMachineConfig))
}

export const applicationQuestionMachineConfigCalculated = (
  machineConfig: QuestionMachineConfig,
): ApplicationQuestionMachineConfigCalculated => ({
  type: ActionTypeKeys.APPLICATION_QUESTION_MACHINE_CONFIG_CALCULATED,
  machineConfig,
})
